import React from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { COUPON_USAGE } from 'api/graphql/coupon';

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
const fmtMoney = (n, c) => `$${Number(n || 0).toLocaleString('es-CO')} ${c || ''}`.trim();

/**
 * Panel de métricas de uso de cupones: quién, qué plan, fecha, monto, descuento.
 */
function CouponMetrics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['coupons']);
  const { data, loading } = useQuery(COUPON_USAGE, { fetchPolicy: 'cache-and-network' });
  const rows = data?.couponUsage ?? [];

  if (loading && rows.length === 0) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>;
  }
  if (rows.length === 0) {
    return <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>{t('noUsage', { ns: 'coupons', defaultValue: 'Aún no hay usos de cupones.' })}</Box>;
  }

  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
      {isMobile ? (
        <Box>
          {rows.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Stack key={i} spacing={0.25} sx={{ px: 1.5, py: 1.25, borderBottom: (th) => `2px solid ${th.palette.stackedRowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{r.user_name || '—'}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{fmtMoney(r.amount, r.currency)}</Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.plan_name} · {fmtDate(r.date)} · -{fmtMoney(r.discount, r.currency)}</Typography>
            </Stack>
          ))}
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('user', { ns: 'coupons', defaultValue: 'Usuario' })}</TableCell>
                <TableCell>{t('plan', { ns: 'coupons', defaultValue: 'Plan' })}</TableCell>
                <TableCell>{t('date', { ns: 'coupons', defaultValue: 'Fecha' })}</TableCell>
                <TableCell align="right">{t('amount', { ns: 'coupons', defaultValue: 'Monto' })}</TableCell>
                <TableCell align="right">{t('discount', { ns: 'coupons', defaultValue: 'Descuento' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={i} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.user_name || '—'}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.user_email}</Typography>
                  </TableCell>
                  <TableCell>{r.plan_name || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(r.amount, r.currency)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'gold.main', fontWeight: 700 }}>-{fmtMoney(r.discount, r.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default CouponMetrics;
